import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { Badge } from "@/components/Badge";
import { TipoIcon } from "@/components/TipoIcon";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { PraticheSkeleton } from "@/components/PraticheSkeleton";
import { STAGE_COLORS, TIPO_BADGE } from "@/lib/constants";
import { getPratiche } from "@/api/pratiche";
import { ApiError } from "@/api/client";
import { formatDateTime } from "@/lib/dates";
import type { Pratica } from "@/types";

const columns = [
  { key: "nome", header: "Pratica", render: (p: Pratica) => <span className="font-medium text-text">{p.nome}</span> },
  { key: "offerta", header: "Offerta", render: (p: Pratica) => <span className="text-muted-foreground">{p.offerta}</span> },
  { key: "tipo", header: "Tipo", render: (p: Pratica) => { const t = TIPO_BADGE[p.tipo] ?? { label: p.tipo, bg: "bg-gray-100", text: "text-gray-800" }; return <Badge bg={t.bg} text={t.text} className="gap-1"><TipoIcon tipo={p.tipo} />{t.label}</Badge>; }},
  { key: "stage", header: "Stato", render: (p: Pratica) => { const s = STAGE_COLORS[p.stage] ?? { bg: "bg-gray-100", text: "text-gray-800" }; return <Badge bg={s.bg} text={s.text}>{p.stage}</Badge>; }},
  { key: "data", header: "Data", render: (p: Pratica) => <span className="text-muted-foreground">{formatDateTime(p.dataCreazione)}</span> },
];

export function Pratiche() {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState("Tutte");
  const [pratiche, setPratiche] = useState<Pratica[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getPratiche()
      .then(setPratiche)
      .catch((e) => {
        if (e instanceof ApiError && e.status >= 500) {
          setError("Errore del sistema CRM. Riprova tra qualche istante.");
        } else if (e instanceof ApiError && e.status === 401) {
          // AuthContext handles 401 — let it through silently
          setError("Sessione scaduta. Ricarica la pagina per accedere di nuovo.");
        } else {
          setError("Impossibile caricare le pratiche.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <PraticheSkeleton />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (pratiche.length === 0) {
    return (
      <EmptyState
        icon={<ClipboardList size={26} className="text-brand" />}
        title="Nessuna pratica ancora"
        description="Per iniziare una nuova pratica, avvia una consulenza."
      />
    );
  }

  const filtered =
    filtro === "Tutte"
      ? pratiche
      : filtro === "In corso"
      ? pratiche.filter((p) => p.stage !== "Completata" && p.stage !== "Annullata")
      : pratiche.filter((p) => p.stage === "Completata");

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-text">Pratiche</h2>
        <select
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          className="px-3 py-2 border border-border rounded-lg text-sm bg-card"
        >
          <option>Tutte</option>
          <option>In corso</option>
          <option>Completate</option>
        </select>
      </div>
      <DataTable columns={columns} data={filtered} onRowClick={(p) => navigate(`/pratiche/${p.id}`)} />
    </div>
  );
}
